import { useEffect, useState } from 'react';
import { Button, Input, Label } from '@staff-app/ui';
import { assessWarranty, type DeviceRmaRequest } from '@staff-app/shared-types';
import { ExternalLink, Search, ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { useRmaOrderLookup } from '../hooks/useRma';

/**
 * RmaWarrantyPanel — the order date, and what it means for eligibility.
 *
 * WHY IT IS HERE (requester, 2026-09-22). "Confirm RMA eligibility" asks a verifier to decide
 * whether a device is still covered, and the fact that decides it — the purchase date — was the one
 * thing the dialog did not show. They had to leave, find the order, work out the anniversary, and
 * come back. This puts the date and both clocks in front of the decision.
 *
 * TWO CLOCKS, NOT ONE, because they answer different questions (KB: rma-warranty-process):
 *   13-month manufacturer warranty → may we replace this device
 *   30-day new-and-unused return   → may they still have a refund instead
 * A device can be well inside the first and long past the second, which is exactly the case where
 * quoting the wrong one starts an argument with a customer.
 *
 * ⚠️ THE DATE IS OFTEN NOT LOOKUP-ABLE, and the panel is built around that rather than treating it
 * as an edge case. Measured against production 2026-09-22: 8 of 15 real RMAs are Shopify purchases
 * and 7 are Amazon, with order numbers like `701-9684402-1340233`, `Optisign order #21509` and
 * `UQNXWXWU-0001`. So "look it up" is a convenience that works about half the time, and TYPING the
 * date is a first-class path, not a fallback.
 *
 * THE VERDICT IS LIVE. It is computed from the date in the box, so looking an order up in Shopify
 * answers "is this covered" immediately rather than after a save. The rule is shared with the
 * server (`@staff-app/shared-types/warranty`), so the badge cannot disagree with the row once saved.
 *
 * ADVISORY, NEVER A GATE. Nothing here disables Confirm. A device that failed on day 370 with a
 * manufacturing defect is a judgement call, and a warranty that lapsed while the customer waited on
 * us is one we would usually honour. The panel states the facts; the human decides.
 */
/**
 * `2024-05-28` → `28 May 2024`.
 *
 * Formatted for reading, not for machines: this line is scanned by a person deciding whether a
 * device is covered, and `2024-05-28` next to `2025-05-28` takes a beat to tell apart while
 * `28 May 2024` and `28 May 2025` do not. Forced to UTC — the value is a calendar day, and letting
 * the browser localise it would shift the date by one for anybody west of Greenwich.
 */
function human(date: string | null | undefined): string {
  if (!date) return '—';
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return String(date);
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * HOW EACH VERDICT LOOKS, in one place (requester, 2026-09-22: "if it is expired, it should show in
 * red; if it is not expired, green").
 *
 * Semantic tokens, not raw `red-600`/`green-600`: this app themes light and dark, and
 * `text-success` / `text-destructive` are what the other 200-odd usages already read. The tint is
 * deliberately light — the panel states a fact, and a saturated block would shout at a verifier on
 * every ordinary in-warranty request.
 */
const VERDICT = {
  IN_WARRANTY: {
    Icon: ShieldCheck,
    box: 'border-success/40 bg-success/10 text-success',
    title: 'In warranty',
    lead: 'Within the 13-month manufacturer warranty — eligible for a replacement.',
  },
  EXPIRED: {
    Icon: ShieldAlert,
    box: 'border-destructive/40 bg-destructive/10 text-destructive',
    title: 'Warranty expired',
    // "not eligible for a WARRANTY replacement" (requester, 2026-09-22) — a flat verdict, and the
    // qualifier is load-bearing rather than hedging. The button below is still pressable, because
    // a device that failed while the customer was waiting on us is a call fulfilment can make; what
    // this sentence settles is that the warranty is not the reason it would be made.
    lead: 'Past the 13-month window — not eligible for a warranty replacement.',
  },
  UNKNOWN: {
    Icon: ShieldQuestion,
    box: 'border-warning/40 bg-warning/10 text-warning',
    title: 'Warranty unknown',
    // Parallel to the other two: each says what the state means for eligibility, so the three read
    // as one sentence with three answers rather than three unrelated notices.
    lead: 'No purchase date — eligibility cannot be judged. Look the order up, or enter it from the receipt.',
  },
} as const;

export function RmaWarrantyPanel({
  row,
  orderNumber,
  value,
  onChange,
}: {
  row: DeviceRmaRequest;
  /** The order number as it stands in the dialog right now — not the saved one. */
  orderNumber: string;
  /** `YYYY-MM-DD`, or '' when not established. Owned by the dialog so Confirm can save it. */
  value: string;
  onChange: (next: string) => void;
}) {
  const [lookup, { loading }] = useRmaOrderLookup();
  const [result, setResult] = useState<{
    found: boolean;
    orderName?: string | null;
    orderDate?: string | null;
    customerEmail?: string | null;
    orderUrl?: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Look up ONCE on open, and only when there is something to look up and no date established yet.
  // Not on every keystroke (it is a live Shopify read) and not when the verifier already has a
  // date — re-fetching would risk overwriting what they decided with what Shopify thinks.
  useEffect(() => {
    if (!orderNumber.trim() || value) return;
    void run(orderNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (number: string) => {
    setError(null);
    try {
      const res = await lookup({ variables: { orderNumber: number.trim() } });
      const out = res.data?.rmaOrderLookup ?? null;
      setResult(out);
      // Fills a BLANK date only. Never overwrites one the verifier has already established —
      // they may have taken it from an Amazon invoice that Shopify knows nothing about.
      if (out?.found && out.orderDate && !value) onChange(out.orderDate);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /**
   * THE VERDICT IS COMPUTED FROM THE DATE IN THE BOX, not from the saved row (requester,
   * 2026-09-22: "once I click look up in Shopify, it should look for the warranty").
   *
   * The server derives the same verdict from the SAVED date, and that is the right source for the
   * queue and the drawer. Here it is the wrong one: the verifier has just pulled a date out of
   * Shopify and saved nothing, and making them press Confirm to find out whether the device is
   * covered inverts the decision — the answer is what they press Confirm *because of*.
   *
   * Same rule on both sides (`@staff-app/shared-types/warranty`, twinned with the API's copy and
   * pinned by one shared table of cases), so the badge cannot say one thing now and another after
   * the save.
   */
  const live = assessWarranty(value || null);
  const state = live.state;
  const days = live.daysRemaining;
  const owned = live.daysOwned;
  const endsOn = live.endsOn;
  const inReturnWindow = live.withinReturnWindow;

  // Saved or not is now a separate question from what the warranty says — the badge is live, and
  // this line says whether the row behind it has caught up yet.
  const unsaved = (value || '') !== (row.verifiedOrderDate ?? '');

  const v = VERDICT[state];
  const { Icon } = v;

  return (
    <div className="rounded-md border" data-testid="rma-warranty">
      {/* THE VERDICT FIRST, and in colour, because it is the answer the verifier came for. The
          controls that produce it sit underneath: you read the conclusion, then adjust the input
          that drove it — the reverse of the form-first layout this replaced, where the answer was
          a small badge at the end of a row of widgets. */}
      <div
        className={`flex items-start gap-3 rounded-t-md border-b px-3 py-2.5 ${v.box}`}
        data-testid="rma-warranty-banner"
        data-state={state}
      >
        <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
        <div className="min-w-0">
          <p className="text-sm font-semibold" data-testid="rma-warranty-badge" data-state={state}>
            {v.title}
            {state === 'IN_WARRANTY' && days != null
              ? ` · ${days} day${days === 1 ? '' : 's'} left`
              : state === 'EXPIRED' && days != null
                ? ` · ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`
                : ''}
          </p>
          <p className="text-xs opacity-90">{v.lead}</p>
        </div>
      </div>

      <div className="flex flex-col gap-3 p-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[10rem]">
            <Label htmlFor="rma-order-date" className="text-xs">
              {'Order date'}
            </Label>
            <Input
              id="rma-order-date"
              type="date"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="mt-1 font-mono"
              data-testid="rma-order-date"
            />
          </div>

          {/* DEFAULT size, not `sm`, so it is exactly as tall as the date box beside it: `Input` is
              h-9 and a `sm` button is h-8, which read as a 4px misalignment in a row whose two
              controls do one job together. */}
          <Button
            variant="outline"
            className="gap-1"
            disabled={loading || !orderNumber.trim()}
            onClick={() => void run(orderNumber)}
            data-testid="rma-order-lookup"
          >
            <Search className="h-4 w-4" />
            {loading ? 'Looking…' : 'Look up in Shopify'}
          </Button>
        </div>

        {/* The arithmetic behind the verdict, in one line, so the badge is checkable rather than
            taken on trust. */}
        {endsOn ? (
          <p className="text-xs text-muted-foreground" data-testid="rma-warranty-detail">
            {`Bought ${human(value)} · day ${owned} of ownership · covered to ${human(endsOn)}`}
            {inReturnWindow
              ? ' · still inside the 30-day return window, so a refund is also possible'
              : ''}
          </p>
        ) : null}

        {(unsaved || result || error) ? (
          <div className="flex flex-col gap-1 border-t pt-2 text-xs text-muted-foreground">
            {unsaved ? (
              <p data-testid="rma-warranty-unsaved">
                {'This date is not saved yet — press Confirm eligible (or Save) to keep it on the request.'}
              </p>
            ) : null}

            {result && !result.found ? (
              <p data-testid="rma-lookup-miss">
                {`No Shopify order matches "${orderNumber.trim()}". That is expected for an Amazon purchase — enter the date from the customer's receipt instead.`}
              </p>
            ) : null}

            {result?.found ? (
              <p data-testid="rma-lookup-hit">
                {`Shopify ${result.orderName} placed ${human(result.orderDate)}`}
                {result.customerEmail ? ` · ordered by ${result.customerEmail}` : ''}
                {result.orderUrl ? (
                  <>
                    {' · '}
                    <a
                      href={result.orderUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 underline"
                      data-testid="rma-lookup-link"
                    >
                      {'open'}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </>
                ) : null}
              </p>
            ) : null}

            {/* ITS OWN LINE, in the warning colour, rather than a clause tacked onto the end of the
                lookup result. "⚠ different from the RMA contact" is the one thing here that says
                the date might belong to somebody else's purchase, and it was reading as a footnote
                to a sentence about an order number. */}
            {result?.found &&
            result.customerEmail &&
            row.customerEmail &&
            result.customerEmail.toLowerCase() !== row.customerEmail.toLowerCase() ? (
              <p className="text-warning" data-testid="rma-lookup-mismatch">
                {`⚠ That order was placed by ${result.customerEmail}, but this RMA was filed by ${row.customerEmail} — check it is the right order before dating the warranty from it.`}
              </p>
            ) : null}

            {error ? (
              <p className="text-destructive" data-testid="rma-lookup-error">
                {error}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
